import { useId } from "react";
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from "react-hook-form";

import { InputText } from "@fielmann-ag/prisma-components";

import { useBlurFieldErrorVisibility } from "@/hooks/useBlurFieldErrorVisibility";
import { useI18n } from "@/i18n";
import { joinClassNames } from "@/lib/utils";
import { getVisibleError } from "@/utils/form/getVisibleError";
import { validateEmail } from "@/validation";

/** Mute invited email: darker fill + `opacity-60` (Prisma disabled is 0.25). */
const READ_ONLY_EMAIL_CLASSES =
  "cursor-default !bg-background-emphasis opacity-60 hover:after:!inset-ring-line-stroke-strong";

type EmailFormValues = FieldValues & { email: string };

interface EmailFieldProps<TFieldValues extends EmailFormValues> {
  control: Control<TFieldValues>;
  label: string;
  dismissFormErrors: () => void;
  /** Extra error state from outside the field (e.g. login credential failure). */
  hasExternalError?: boolean;
  /** Keeps a trusted email (e.g. from an invitation) visible but not editable. */
  isReadOnly?: boolean;
  className?: string;
}

/**
 * Email Controller shared by login, signup, and reset-request.
 *
 * Owns format validation, blur-gated inline errors, and dismiss-on-edit.
 * The form must include an `email` field; the Controller path is not configurable.
 */
export function EmailField<TFieldValues extends EmailFormValues>({
  control,
  label,
  dismissFormErrors,
  hasExternalError = false,
  isReadOnly = false,
  className,
}: EmailFieldProps<TFieldValues>) {
  const { messages } = useI18n();
  const emailErrorVisibility = useBlurFieldErrorVisibility();
  const errorId = useId();

  return (
    <Controller
      control={control}
      name={"email" as Path<TFieldValues>}
      rules={{
        validate: (value) =>
          validateEmail(String(value ?? ""), messages.validation),
      }}
      render={({ field, fieldState }) => {
        if (isReadOnly) {
          return (
            <InputText
              label={label}
              value={String(field.value ?? "")}
              onChange={() => undefined}
              isReadOnly
              isRequired
              className={joinClassNames(READ_ONLY_EMAIL_CLASSES, className)}
            />
          );
        }

        const error = getVisibleError(
          fieldState.error,
          emailErrorVisibility.showError,
        );
        const isInvalid = Boolean(error) || hasExternalError;

        return (
          <InputText
            label={label}
            value={String(field.value ?? "")}
            onChange={(value) => {
              dismissFormErrors();
              emailErrorVisibility.hideError();
              field.onChange(value);
            }}
            onBlur={() => {
              field.onBlur();
              emailErrorVisibility.revealError();
            }}
            state={isInvalid ? "error" : undefined}
            isInvalid={isInvalid}
            isRequired
            aria-describedby={error ? errorId : undefined}
            helperText={
              error ? { state: "error", label: error, id: errorId } : undefined
            }
            className={className}
          />
        );
      }}
    />
  );
}
